import { ChevronRight } from '@mui/icons-material';
import {
  alpha,
  ButtonBase,
  Grid,
  styled,
  Theme,
  Typography,
  type DialogProps,
} from '@mui/material';
import { BANK_RECONCILIATION } from 'adminConstants/routes';
import GridContainer from 'components/GridContainer';
import PageLoader from 'components/PageLoader';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import CountrySelector from 'further-ui/components/CountrySelector';
import FormField from 'further-ui/components/FormField';
import { Column, Divider } from 'further-ui/components/Layout';
import SearchField from 'further-ui/components/SearchField';
import TypographyButton from 'further-ui/components/TypographyButton';
import { countries } from 'further-ui/labels';
import { useNotification } from 'hooks/ui/useNotification';
import api from 'lib/trpcClient';
import { useState } from 'react';
import EmptyState from './EmptyState';

const Image = styled('img')(({ theme }: { theme: Theme }) => ({
  maxWidth: '50px',
  maxHeight: '50px',
  borderRadius: theme.shape.borderRadius,
}));

const BankItem = styled(ButtonBase)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(theme.spacingSizes.sm),
  width: '100%',
  padding: theme.spacing(theme.spacingSizes.xs),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  height: '60px',

  '&:hover, &.Mui-focusVisible': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
}));

const BankName = styled(Typography)(() => ({
  flexGrow: 1,
}));

const Icon = styled(ChevronRight)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

type Props = {
  open: boolean;
  onClose: () => void;
  onAddManually: () => void;
  transitionDuration?: DialogProps['transitionDuration'];
  bankAccountId?: string;
};

const BankSearch: React.FC<Props> = ({
  open,
  onClose,
  onAddManually,
  transitionDuration,
  bankAccountId,
}) => {
  const { error } = useNotification();
  const [selectedCountry, setSelectedCountry] = useState<string>();
  const [searchQuery, setSearchQuery] = useState<string>();

  const selectedCountryCode = countries.find(
    (country) => country.name === selectedCountry,
  )?.code;

  const { data, isFetching } = api.bankFeed.listBanks.useQuery(
    { country: selectedCountryCode ?? '' },
    { enabled: !!selectedCountryCode },
  );

  const authoriseBank = api.bankFeed.authoriseBank.useMutation({
    onSuccess: (data) => {
      if (!data) {
        error('Failed to authorise bank.');
        return;
      }
      window.location.href = data.redirectUrl;
    },
  });

  const handleSelectBank = (bankId: string) => {
    const bankAccountParam = bankAccountId
      ? `&bankAccountId=${bankAccountId}`
      : '';
    authoriseBank.mutate({
      institutionId: bankId,
      redirectUrl: `${window.location.protocol}//${window.location.host}${BANK_RECONCILIATION}?drawer=processBankAccount${bankAccountParam}`,
    });
  };

  const availableBanks = (data ?? []).filter(
    (bank) =>
      !searchQuery ||
      bank.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleCountryChange = (country: string | undefined) => {
    setSelectedCountry(country);
    setSearchQuery(undefined);
  };

  const renderResults = () => {
    if (isFetching || authoriseBank.isPending) return <PageLoader />;
    if (!data?.length) {
      return (
        <EmptyState
          text="We do not support quick-connect to banks in this country yet."
          button={{ label: 'Add account manually', onClick: onAddManually }}
        />
      );
    }
    if (!availableBanks.length) {
      return (
        <EmptyState
          text="No results match your search."
          button={{ label: 'Add account manually', onClick: onAddManually }}
        />
      );
    }
    return (
      <GridContainer>
        {availableBanks.map((bank) => (
          <Grid key={bank.id} size={{ xs: 12, md: 6 }}>
            <BankItem onClick={() => handleSelectBank(bank.id)}>
              <Image src={bank.logo} alt={bank.name} />
              <BankName variant="h6">{bank.name}</BankName>
              <Icon />
            </BankItem>
          </Grid>
        ))}
      </GridContainer>
    );
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      transitionDuration={transitionDuration}
      title="Connect bank account"
      maxWidth="md"
      fullHeight
      content={
        <Column spacing="lg" fullHeight>
          <FormField label="Country" size="md">
            <Column spacing="sm">
              <CountrySelector
                fullWidth
                value={selectedCountry}
                onChange={handleCountryChange}
                placeholder="Select country"
                showPopularFirst
              />
              <Typography variant="body2">
                Bank not listed?{' '}
                <TypographyButton text="Click here" onClick={onAddManually} />{' '}
                to add an account manually.
              </Typography>
            </Column>
          </FormField>
          <Divider />
          {selectedCountry ? (
            <>
              {data?.length ? (
                <SearchField
                  value={searchQuery ?? ''}
                  autoSearchOnDebounce
                  onSearch={setSearchQuery}
                  placeholder="Search by bank"
                  fullWidth
                  allowEmptyQuery
                  iconPlacement="right"
                />
              ) : null}
              {renderResults()}
            </>
          ) : (
            <EmptyState text="Over 10,000 banks supported across 48 countries." />
          )}
        </Column>
      }
    />
  );
};

export default BankSearch;
